import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card,
  CardTitle,
  CardBody,
  Button,
  Table,
  Input,
  Row,
  Col,
  Label,
} from 'reactstrap';
import ReactPaginate from 'react-paginate';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { apiService } from '../../constants/ApiService';
import VendorBranchSelector from '../../components/VendorBranchSelector';
import { toast } from 'react-toastify';
import useVendorBranch from '../../utils/useVendorBranch';
import conditionalDataLoad from '../../utils/conditionalDataLoad';
import { useSelector } from 'react-redux';
import { showDefaultValue } from '../../constants/DateFormatter';
import {
  createAppConnection,
  joinModuleGroup,
  leaveModuleGroup,
  subscribeRealtimeUpdate,
  unsubscribeRealtimeUpdate,
} from '../../utils/signalRService';

const MODULE_NAME = 'Enrollment';

const EnrollmentList = () => {
  const { user } = useSelector((state) => state.authState);

  const { isSuperAdmin, isAdmin, hasMultiBranch, hasSingleBranch } =
    useVendorBranch(user);

  const [selectionData, setSelectionData] = useState({
    vendor_id: '',
    branch_id: '',
  });

  const [errorMessage, setErrorMessage] = useState({});
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [totalPage, setTotalPage] = useState(1);
  const [search, setSearch] = useState('');

  const [classOptions, setClassOptions] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');

  const [sessionOptions, setSessionOptions] = useState([]);
  const [selectedSessionYear, setSelectedSessionYear] = useState('');

  const [batchMasterData, setBatchMasterData] = useState([]);
  const [batchId, setBatchId] = useState('');

  const currentGroupRef = useRef({
    module: null,
    vendorId: null,
    branchId: null,
  });

  const canLoadNow = () =>
    conditionalDataLoad({
      hasMultiBranch,
      hasSingleBranch,
      isSuperAdmin,
      isAdmin,
      vendorId: selectionData.vendor_id,
      branchId: selectionData.branch_id,
    });

  const fetchClasses = async (searchText = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      // if (!vendorId || !branchId) {
      //   setClassOptions([]);
      //   return;
      // }

      const res = await apiService.get(
        `api/ClassMaster/GetClassMaster?vendorId=${vendorId}&branchId=${branchId}&searchItem=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res?.status === true) {
        const raw = res?.data || [];
        const opts = (raw || [])
          .map((x) => ({
            value: String(x.id ?? x.ID ?? x.class_id ?? x.classId ?? ''),
            label: String(
              x.class_display_name ??
                x.class_Name ??
                x.className ??
                x.name ??
                'No Name',
            ),
          }))
          .filter((o) => o.value && o.value !== '0');

        setClassOptions(opts);
      } else {
        setClassOptions([]);
      }
    } catch (err) {
      console.error(err);
      setClassOptions([]);
    }
  };

  const fetchBatches = async (course_selection_id = '', searchText = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      // if (!vendorId || !branchId) {
      //   setBatchMasterData([]);
      //   return;
      // }

      const res = await apiService.get(
        `api/BatchMaster/GetAllBatch?vendorId=${vendorId}&branchId=${branchId}&classId=${course_selection_id || ''}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      if (res.status === true) {
        const list = res.data || [];
        setBatchMasterData(
          (list || []).map((x) => ({
            value: x.id,
            label: x.batch_code
              ? `${x.batch_code} - ${x.batch_start_time}`
              : 'Batch code not found',
          })),
        );
      } else {
        setBatchMasterData([]);
        toast.error(res.message || 'Failed to fetch batches');
      }
    } catch (err) {
      console.log(err);
      setBatchMasterData([]);
      toast.error('Failed to load batches');
    }
  };

  const fetchSessions = async (searchText = '') => {
    try {
      const vendorId = selectionData.vendor_id || '';
      const branchId = selectionData.branch_id || '';

      // if (!vendorId || !branchId) {
      //   setSessionOptions([]);
      //   return;
      // }

      const res = await apiService.get(
        `api/SessionMaster/GetAllSession?vendorId=${vendorId}&branchId=${branchId}&search=${encodeURIComponent(
          searchText || '',
        )}`,
      );

      const container = res?.data ?? res;

      const list =
        (Array.isArray(container?.data) && container.data) ||
        (Array.isArray(container?.Data) && container.Data) ||
        (Array.isArray(container) && container) ||
        [];

      const opts = (list || [])
        .map((x) => {
          const name =
            x.session_name ??
            x.sessionName ??
            x.session_year ??
            x.sessionYear ??
            '';
          const sessionStr = String(name || '').trim();
          return sessionStr ? { value: sessionStr, label: sessionStr } : null;
        })
        .filter(Boolean);

      const unique = Array.from(
        new Map(opts.map((o) => [o.value, o])).values(),
      );
      setSessionOptions(unique);
    } catch (err) {
      console.error(err);
      setSessionOptions([]);
    }
  };

  const GetEnrollmentList = useCallback(
    async (
      page = 1,
      limit = 10,
      searchText = '',
      classId = '',
      selectedBatchId = '',
      sessionYear = '',
    ) => {
      try {
        const vendorId = selectionData.vendor_id || '';
        const branchId = selectionData.branch_id || '';

        // if (!vendorId || !branchId) {
        //   setTableData([]);
        //   setTotalPage(1);
        //   return;
        // }

        const params = new URLSearchParams();
        params.append('vendorId', vendorId);
        params.append('branchId', branchId);
        params.append('search', searchText || '');

        if (classId) params.append('classId', String(classId));
        if (selectedBatchId) params.append('batchId', String(selectedBatchId));
        if (sessionYear) params.append('sessionYear', String(sessionYear));

        const url = `api/Enrollment/GetAllEnrollment?${params.toString()}`;
        const res = await apiService.get(url);

        if (res?.response && res?.response?.data?.errors) {
          toast.error('Validation error');
          return;
        }

        if (res?.status === true) {
          setTableData(Array.isArray(res.data) ? res.data : []);
          setTotalPage(1);
        } else {
          setTableData([]);
        }
      } catch (err) {
        console.error(err);
        toast.error('Something went wrong while loading enrollments');
        setTableData([]);
      }
    },
    [selectionData.vendor_id, selectionData.branch_id],
  );

  useEffect(() => {
    //if (!canLoadNow()) return;

    setSelectedClassId('');
    setSelectedSessionYear('');
    setBatchId('');
    setSearch('');

    fetchClasses('');
    fetchBatches('');
    fetchSessions('');
    GetEnrollmentList(1, 10, '', '', '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionData.vendor_id, selectionData.branch_id]);

  useEffect(() => {
    if (!canLoadNow()) return;

    GetEnrollmentList(
      1,
      10,
      search,
      selectedClassId,
      batchId,
      selectedSessionYear,
    );
  }, [
    selectedClassId,
    selectedSessionYear,
    batchId,
    search,
    GetEnrollmentList,
  ]);

  useEffect(() => {
    let mounted = true;

    const setupSignalR = async () => {
      try {
        await createAppConnection();

        const vendorId = selectionData.vendor_id || 0;
        const branchId = selectionData.branch_id || 0;

        const prev = currentGroupRef.current;

        if (
          prev.vendorId &&
          prev.branchId &&
          (String(prev.vendorId) !== String(vendorId) ||
            String(prev.branchId) !== String(branchId))
        ) {
          await leaveModuleGroup(prev.module, prev.vendorId, prev.branchId);
        }

        if (vendorId && branchId) {
          await joinModuleGroup(MODULE_NAME, vendorId, branchId);

          currentGroupRef.current = {
            module: MODULE_NAME,
            vendorId,
            branchId,
          };
        }

        unsubscribeRealtimeUpdate();

        await subscribeRealtimeUpdate((payload) => {
          if (!mounted) return;
          if (!payload) return;

          const isSameModule = String(payload.module || '') === MODULE_NAME;
          const isSameVendor =
            String(payload.vendorId || '') === String(vendorId || '');
          const isSameBranch =
            String(payload.branchId || '') === String(branchId || '');

          if (isSameModule && isSameVendor && isSameBranch) {
            console.log('Enrollment realtime update received:', payload);
            GetEnrollmentList(
              1,
              10,
              search,
              selectedClassId,
              batchId,
              selectedSessionYear,
            );
          }
        });
      } catch (err) {
        console.error('SignalR error:', err);
      }
    };

    if (canLoadNow()) {
      setupSignalR();
    }

    return () => {
      mounted = false;
    };
  }, [
    selectionData.vendor_id,
    selectionData.branch_id,
    hasMultiBranch,
    hasSingleBranch,
    isSuperAdmin,
    isAdmin,
    GetEnrollmentList,
    search,
    selectedClassId,
    batchId,
    selectedSessionYear,
  ]);

  useEffect(() => {
    return () => {
      unsubscribeRealtimeUpdate();

      const prev = currentGroupRef.current;
      if (prev?.vendorId && prev?.branchId) {
        leaveModuleGroup(prev.module, prev.vendorId, prev.branchId).catch(
          console.error,
        );
      }
    };
  }, []);

  const onHandleAdd = () => navigate('/enrollment-form');

  const onHandleEdit = (row) => {
    if (!row) {
      toast.error('Record not found in list');
      return;
    }

    navigate('/enrollment-form', { state: { ...row } });
  };

  const onHandleDelete = async (id) => {
    if (!window.confirm('Are you sure to delete?')) return;

    try {
      const res = await apiService.delete(
        `api/Enrollment/DeleteEnrollment?id=${id}`,
      );

      const root = res && res.data !== undefined ? res.data : res || {};
      if (root.status === true) {
        toast.success(root.Message || root.message || 'Deleted');
        GetEnrollmentList(
          1,
          10,
          search,
          selectedClassId,
          batchId,
          selectedSessionYear,
        );
      } else {
        toast.error(root.Message || root.message || 'Delete failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Something went wrong deleting the record');
    }
  };

  const onSearchChange = (e) => {
    const q = e.target.value;
    setSearch(q);
    GetEnrollmentList(1, 10, q, selectedClassId, batchId, selectedSessionYear);
  };

  return (
    <>
      <div className='text-right mb-2'>
        <Button color='primary' onClick={onHandleAdd}>
          <i className='bi bi-plus-circle' /> New Enrollment
        </Button>
      </div>

      <VendorBranchSelector
        user={user}
        selectionData={selectionData}
        setSelectionData={setSelectionData}
        errorMessage={errorMessage}
        accessButton={true}
      />

      <Card className='card-box height-100-p pd-20 mb-20'>
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3'>
          <div>
            <h4 className='fw-bold text-primary mb-1'>
              <i className='bi bi-mortarboard mr-2'></i>All Enrollments
            </h4>
            <small className='text-muted'>
              Showing <span className='fw-semibold'>{tableData.length}</span>{' '}
              records
            </small>
          </div>

          <div className='position-relative w-50 w-md-50'>
            <input
              type='search'
              placeholder='Search...'
              value={search}
              onChange={onSearchChange}
              className='form-control ps-5 rounded-pill'
            />
          </div>
        </div>

        <div className='row g-3 mb-4'>
          <Col md='6'>
            <Label className='mb-1'>Select a Course</Label>
            <Select
              options={classOptions}
              value={
                classOptions.find(
                  (o) => String(o.value) === String(selectedClassId),
                ) || null
              }
              onChange={(opt) => {
                const nextClassId = opt ? String(opt.value) : '';
                setSelectedClassId(nextClassId);
                setBatchId('');
                fetchBatches(nextClassId, '');
              }}
              onInputChange={(val, meta) => {
                if (meta.action === 'input-change') fetchClasses(val);
              }}
              isClearable
              isSearchable
              placeholder='Type a Course Name...'
            />
          </Col>

          <Col md='6'>
            <Label className='mb-1'>Select a Batch</Label>

            <Select
              options={batchMasterData}
              value={batchMasterData.find((x) => String(x.value) === String(batchId)) || null}
              onChange={(option) => {
                if (option) {
                  setBatchId(String(option.value));
                } else {
                  setBatchId('');
                }
              }}
              onInputChange={(inputValue, actionMeta) => {
                if (actionMeta.action === 'input-change') {
                  fetchBatches(selectedClassId, inputValue);
                }
              }}
              isSearchable
              isClearable
              placeholder='Search batches ...'
              styles={{
                control: (base) => ({ ...base, height: '38px' }),
              }}
            />
          </Col>

          {/* session filter if needed
          <Col md='6'>
            <Label className='mb-1'>Select a Session</Label>
            <Select
              options={sessionOptions}
              value={
                sessionOptions.find(
                  (o) => String(o.value) === String(selectedSessionYear),
                ) || null
              }
              onChange={(opt) =>
                setSelectedSessionYear(opt ? String(opt.value) : '')
              }
              onInputChange={(val, meta) => {
                if (meta.action === 'input-change') fetchSessions(val);
              }}
              isClearable
              isSearchable
              placeholder='Type and Choose a Session...'
            />
          </Col>
          */}
        </div>

        <div
          className='col-12 d-flex justify-content-center'
          style={{ paddingRight: '10px', paddingLeft: '10px' }}
        />

        <CardBody>
          <div
            className='table-responsive'
            style={{
              overflowX: 'auto',
              maxWidth: '150vh',
              whiteSpace: 'nowrap',
            }}
          >
            <Table striped hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th></th>
                  <th>Enroll No.</th>
                  {/* <th>Roll No.</th> */}
                  <th>Student Name</th>
                  <th>Course Name</th>
                  <th>Faculty Name</th>
                  <th>Batch Name</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {(tableData ?? []).length > 0 ? (
                  tableData.map((item, index) => (
                    <tr key={item.enrollmentId ?? index}>
                      <th scope='row'>{index + 1}</th>

                      <td>
                        <i
                          className='bi bi-pencil text-primary'
                          style={{ cursor: 'pointer' }}
                          onClick={() => onHandleEdit(item)}
                          title='Edit'
                        />
                      </td>

                      <td>
                        {showDefaultValue(item.enrollment_number, 'Not Provided')}
                      </td>
                      {/* <td>{showDefaultValue(item.roll_no, 'Not Provided')}</td> */}
                      <td>{item.student_name}</td>
                      <td>{item.course_name}</td>
                      <td>{item.faculty_name}</td>
                      <td>{item.batch_name}</td>

                      <td className='text-center'>
                        <i
                          className='bi bi-trash text-danger'
                          style={{ cursor: 'pointer', fontSize: '1.1rem' }}
                          title='Delete'
                          onClick={() => onHandleDelete(item.enrollmentId)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={9} className='text-center'>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>

        <CardBody>
          <ReactPaginate
            previousLabel={'Previous'}
            nextLabel={'Next'}
            breakLabel={'...'}
            pageCount={totalPage}
            onPageChange={(d) =>
              GetEnrollmentList(
                d.selected + 1,
                10,
                search,
                selectedClassId,
                batchId,
                selectedSessionYear,
              )
            }
            containerClassName={'pagination'}
            activeClassName={'active'}
          />
        </CardBody>
      </Card>
    </>
  );
};

export default EnrollmentList;